import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Play, SkipForward, ListVideo, Check } from 'lucide-react';
import { Playlist } from '../types/playlist';
import { Video } from '../types/video';
import { formatClock } from '../lib/editorTime';
import CustomVideoPlayer from './CustomVideoPlayer';

interface PlaylistPageProps {
  playlist: Playlist;
  videos: Video[];
  onBack: () => void;
  isOwner?: boolean;
  onAddVideo?: (videoId: string) => Promise<void>;
  onRemoveVideo?: (videoId: string) => Promise<void>;
}

/** Small picture of a video: its stored thumbnail, else the first frame, else a placeholder. */
const Thumb: React.FC<{ video: Video; width: number; children?: React.ReactNode }> = ({ video, width, children }) => {
  const src = video.streamUrl || (video as any).stream_url || (video as any).fileUrl || (video as any).file_url;
  return (
    <div style={{
      width, aspectRatio: '16 / 9', flexShrink: 0, borderRadius: 4, overflow: 'hidden',
      background: '#000', position: 'relative',
    }}>
      {video.thumbnail_url ? (
        <img src={video.thumbnail_url} alt="" aria-hidden style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      ) : src ? (
        <video
          src={src}
          preload="metadata"
          muted
          playsInline
          aria-hidden
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
        />
      ) : (
        <div style={{
          width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'linear-gradient(135deg, var(--theme-primary, #4f46e5), var(--surface-selected, #7c3aed))',
          color: '#fff',
        }}>
          <Play size={14} aria-hidden />
        </div>
      )}
      {children}
    </div>
  );
};

const PlaylistPage: React.FC<PlaylistPageProps> = ({
  playlist, videos, onBack, isOwner, onAddVideo, onRemoveVideo,
}) => {
  const { t } = useTranslation();
  const [selectedIndex, setCurrentIndex] = useState(0);
  const [managing, setManaging] = useState(false);
  const [pending, setPending] = useState<Record<string, boolean>>({});
  // No add/remove handlers (e.g. the school is read-only): no managing.
  const canManage = !!isOwner && !!(onAddVideo || onRemoveVideo);
  const showPicker = managing && canManage;

  // Build ordered video list from playlist.videoIds preserving order
  const orderedVideos = playlist.videoIds
    .map((id) => videos.find((v) => v.id === id))
    .filter(Boolean) as Video[];

  // Stays in range when videos are removed from the list while it plays.
  const currentIndex = Math.min(selectedIndex, Math.max(0, orderedVideos.length - 1));
  const current = orderedVideos[currentIndex] ?? null;

  const goNext = () => {
    if (currentIndex < orderedVideos.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const toggle = async (video: Video) => {
    const inPlaylist = playlist.videoIds.includes(video.id);
    setPending((p) => ({ ...p, [video.id]: true }));
    try {
      if (inPlaylist) await onRemoveVideo?.(video.id);
      else await onAddVideo?.(video.id);
    } finally {
      setPending((p) => { const n = { ...p }; delete n[video.id]; return n; });
    }
  };

  return (
    <div className="airec-playlist">
      {/* Video area */}
      <div className="airec-playlist__main">
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '12px 20px',
          borderBottom: '1px solid var(--theme-border, #e5e7eb)',
          background: 'var(--card-bg, var(--theme-card, #fff))',
        }}>
          <button
            onClick={onBack}
            aria-label={t('playlist.back_aria')}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-secondary, #6b7280)', fontSize: 13,
            }}
          >
            <ChevronLeft size={16} aria-hidden />
            {t('playlist.back')}
          </button>
          <div style={{ height: 16, width: 1, background: 'var(--theme-border, #e5e7eb)' }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--card-title-color, var(--text-primary, #111))' }}>
            {playlist.title}
          </span>
          {orderedVideos.length > 0 && (
            <span style={{ fontSize: 12, color: 'var(--text-secondary, #9ca3af)' }}>
              {currentIndex + 1} / {orderedVideos.length}
            </span>
          )}

          {/* Owner management toggle */}
          {canManage && (
            <button
              onClick={() => setManaging((m) => !m)}
              aria-pressed={managing}
              style={{
                marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500,
                border: managing
                  ? '1px solid color-mix(in srgb, var(--theme-primary, #1976d2) 40%, transparent)'
                  : '1px solid var(--theme-border, #e5e7eb)',
                background: managing
                  ? 'color-mix(in srgb, var(--theme-primary, #1976d2) 10%, transparent)'
                  : 'var(--surface-hover, #f3f4f6)',
                color: managing ? 'var(--theme-primary, #1976d2)' : 'var(--text-secondary, #6b7280)',
                cursor: 'pointer',
              }}
            >
              <ListVideo size={13} aria-hidden />
              {managing ? t('playlist.done_managing', 'Done') : t('playlist.manage_videos', 'Manage videos')}
            </button>
          )}
        </div>

        {/* Player */}
        <div className="airec-playlist__player">
          {current ? (
            <CustomVideoPlayer
              src={current.streamUrl || current.url}
              mimeType={current.mimeType}
              knownDuration={current.duration}
              videoId={current.id}
              onEnded={goNext}
              trackViews
              style={{ maxHeight: '100%', maxWidth: '100%', width: '100%' }}
            />
          ) : (
            <div style={{ color: '#fff', fontSize: 14 }}>{t('playlist.no_videos')}</div>
          )}
        </div>

        {/* Current video info + next button */}
        {current && (
          <div style={{
            padding: '12px 20px',
            background: 'var(--card-bg, var(--theme-card, #fff))',
            borderTop: '1px solid var(--theme-border, #e5e7eb)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--card-title-color, var(--text-primary, #111))' }}>
                {current.title}
              </p>
              {current.description && (
                <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>
                  {current.description}
                </p>
              )}
            </div>
            {currentIndex < orderedVideos.length - 1 && (
              <button
                onClick={goNext}
                aria-label={t('playlist.next_aria')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '7px 14px', borderRadius: 6,
                  background: 'var(--theme-primary, #1976d2)', color: 'var(--button-primary-foreground, #fff)',
                  border: 'none', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <SkipForward size={14} aria-hidden />
                {t('playlist.next')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Video list (beside the player; under it on narrow screens): queue, or picker when managing */}
      <div className="airec-playlist__queue">
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--theme-border, #e5e7eb)' }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary, #9ca3af)', textTransform: 'uppercase' }}>
            {showPicker
              ? t('playlist.all_channel_videos', 'Channel videos')
              : t('playlist.in_playlist')}
          </p>
        </div>

        {showPicker ? (
          /* Video picker — thumbnail list with add/remove toggle */
          videos.length === 0 ? (
            <p style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)', padding: '14px' }}>
              {t('playlist.no_channel_videos', 'No videos in the channel yet.')}
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {videos.map((video) => {
                const inPlaylist = playlist.videoIds.includes(video.id);
                const busy = !!pending[video.id];
                return (
                  <button
                    key={video.id}
                    onClick={() => !busy && toggle(video)}
                    disabled={busy}
                    title={video.title}
                    aria-label={inPlaylist
                      ? t('playlist.remove_video_aria', { title: video.title })
                      : t('playlist.add_video_aria', { title: video.title })}
                    style={{
                      width: '100%', textAlign: 'left',
                      padding: '8px 10px',
                      display: 'flex', alignItems: 'center', gap: 10,
                      background: inPlaylist
                        ? 'color-mix(in srgb, var(--theme-primary, #1976d2) 8%, transparent)'
                        : 'transparent',
                      border: 'none', borderBottom: '1px solid var(--theme-border, #e5e7eb)',
                      cursor: busy ? 'not-allowed' : 'pointer',
                      opacity: busy ? 0.6 : 1,
                      transition: 'background 0.12s',
                    }}
                  >
                    <Thumb video={video} width={72}>
                      {inPlaylist && (
                        <div style={{
                          position: 'absolute', inset: 0,
                          background: 'color-mix(in srgb, var(--theme-primary, #1976d2) 60%, transparent)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Check size={18} color="#fff" aria-hidden />
                        </div>
                      )}
                    </Thumb>

                    {/* Title — truncated, full title in native tooltip (title attr above) */}
                    <span style={{
                      flex: 1, fontSize: 12, lineHeight: '1.35',
                      color: inPlaylist ? 'var(--theme-primary, #1976d2)' : 'var(--text-primary, #111)',
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical' as const,
                      WebkitLineClamp: 2,
                      overflow: 'hidden',
                      fontWeight: inPlaylist ? 600 : 400,
                    }}>
                      {video.title}
                    </span>
                  </button>
                );
              })}
            </div>
          )
        ) : (
          /* Queue: number (or ▶ for the one playing), picture with length, title */
          orderedVideos.map((video, idx) => {
            const playing = idx === currentIndex;
            return (
              <button
                key={video.id}
                onClick={() => setCurrentIndex(idx)}
                title={video.title}
                aria-label={t('playlist.play_aria', { title: video.title })}
                aria-current={playing ? 'true' : undefined}
                style={{
                  width: '100%', textAlign: 'left', padding: '8px 10px',
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: playing ? 'color-mix(in srgb, var(--theme-primary, #1976d2) 10%, transparent)' : 'transparent',
                  border: 'none', borderBottom: '1px solid var(--theme-border, #e5e7eb)',
                  cursor: 'pointer',
                  transition: 'background 0.1s',
                }}
              >
                <span style={{
                  minWidth: 16, fontSize: 11, fontWeight: 700, textAlign: 'center',
                  color: playing ? 'var(--theme-primary, #1976d2)' : 'var(--text-secondary, #9ca3af)',
                }}>
                  {playing ? <Play size={11} fill="currentColor" aria-hidden /> : idx + 1}
                </span>
                <Thumb video={video} width={96}>
                  {Number(video.duration) > 0 && (
                    <span style={{
                      position: 'absolute', right: 3, bottom: 3, padding: '0 4px', borderRadius: 3,
                      background: 'rgba(0,0,0,0.75)', color: '#fff', fontSize: 10, fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {formatClock(Number(video.duration))}
                    </span>
                  )}
                </Thumb>
                <span style={{
                  flex: 1, minWidth: 0, fontSize: 12, lineHeight: '1.35', fontWeight: playing ? 600 : 400,
                  color: playing ? 'var(--theme-primary, #1976d2)' : 'var(--text-primary, #111)',
                  display: '-webkit-box', WebkitBoxOrient: 'vertical' as const, WebkitLineClamp: 2, overflow: 'hidden',
                }}>
                  {video.title}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PlaylistPage;
